import React from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { MHMenuItem, MHSelect } from '@styles/material-ui';
import { withTheme } from '@emotion/react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { performNavigate } from '@state/slices/app/routing/routingSlice';
import { useI18NextContext } from '@components/hooks';
import { Filter, FilterOption } from '@model/iceberg';
import { getFilterPaddingMixin } from '@styles/filters';

export interface SortByComponentProps {
  theme?: Theme;
  testId?: string;
  filter: Filter;
  onChange: (sort: Filter) => void;
  isLeftFilter?: boolean;
}

interface ContainerProps extends ThemeProps {
  isLeftFilter?: boolean;
}

const Container: any = styled.div(({ theme, isLeftFilter }: ContainerProps) => ({
  ...getFilterPaddingMixin(theme, isLeftFilter)
}));

const Heading = styled.h5(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  margin: 0,
  marginBottom: 10,
  whiteSpace: 'nowrap'
}));

export const SortByComponent = ({ testId, filter, onChange, isLeftFilter }: SortByComponentProps) => {
  const t: any = useI18NextContext();
  const dispatch = useDispatch();
  const router = useRouter();
  const handleChange = (name: string) => {
    const { pathname, query } = router;
    let selected;
    const update: Array<FilterOption> = filter.values.map((s: FilterOption) => {
      const isSelected = s.name === name;
      if (isSelected) {
        selected = s.value;
      }
      return isSelected ? { ...s, selected: true } : { ...s, selected: false };
    });
    if (selected) {
      dispatch(
        performNavigate({ pathname, query: { ...query, sort: selected } }, undefined, {
          shallow: true
        })
      );
    }
    onChange({
      ...filter,
      values: update
    });
  };

  const renderSortByOptions = () =>
    filter.values.map((option: FilterOption, index) => (
      <MHMenuItem data-testid={TestId.filters.sortByMenuItem} key={`${option}-${index}`} value={option.name}>
        {option.name}
      </MHMenuItem>
    ));
  const selected: FilterOption | undefined = filter.values.find((s: FilterOption) => s.selected) || filter.values[0];

  return (
    <Container data-testid={testId || TestId.filters.sortBy} isLeftFilter={isLeftFilter}>
      <Heading>{t('common__label--sort-by')}:</Heading>
      <MHSelect
        data-testid={TestId.filters.sortBySelect}
        value={selected?.name}
        onChange={(event: any) => handleChange(event.target.value)}
        inputProps={{
          name: 'sort-by',
          id: 'sort-by'
        }}
      >
        {renderSortByOptions()}
      </MHSelect>
    </Container>
  );
};

export const SortBy = withTheme(SortByComponent);
